import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Buildings,
  House,
  ArrowRight,
  SpinnerGap,
  CaretDown,
  CaretRight,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Asset, AssetStatus } from 'shared/types/asset';

interface InstancesTargetPanelProps {
  assets: Asset[];
  isLoading: boolean;
  hasTypeSelected: boolean;
  createdAssetIds: Set<string>;
}

const statusLabels: Record<AssetStatus, string> = {
  [AssetStatus.ACTIVE]: 'Actief',
  [AssetStatus.INACTIVE]: 'Inactief',
  [AssetStatus.MAINTENANCE]: 'Onderhoud',
  [AssetStatus.DEFECT]: 'Defect',
  [AssetStatus.RETIRED]: 'Buiten gebruik',
  [AssetStatus.DECOMMISSIONED]: 'Ontmanteld',
  [AssetStatus.PENDING_INSTALLATION]: 'Wacht op installatie',
};

const statusVariants: Record<AssetStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  [AssetStatus.ACTIVE]: 'default',
  [AssetStatus.INACTIVE]: 'secondary',
  [AssetStatus.MAINTENANCE]: 'outline',
  [AssetStatus.DEFECT]: 'destructive',
  [AssetStatus.RETIRED]: 'secondary',
  [AssetStatus.DECOMMISSIONED]: 'secondary',
  [AssetStatus.PENDING_INSTALLATION]: 'outline',
};

const InstancesTargetPanel: React.FC<InstancesTargetPanelProps> = ({
  assets,
  isLoading,
  hasTypeSelected,
  createdAssetIds,
}) => {
  const navigate = useNavigate();

  // Track which building groups are collapsed (all expanded by default)
  const [collapsedBuildings, setCollapsedBuildings] = useState<Set<string>>(new Set());

  // Group assets by building
  const assetsByBuilding = useMemo(() => {
    const grouped = new Map<string, Asset[]>();
    assets.forEach(asset => {
      const key = asset.buildingId || '__no_building__';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(asset);
    });
    // Sort building keys
    return new Map(
      Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]))
    );
  }, [assets]);

  const toggleBuilding = (buildingId: string) => {
    setCollapsedBuildings(prev => {
      const next = new Set(prev);
      if (next.has(buildingId)) {
        next.delete(buildingId);
      } else {
        next.add(buildingId);
      }
      return next;
    });
  };

  const hasMultipleBuildings = assetsByBuilding.size > 1;

  if (!hasTypeSelected) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4" />
            Voorzieningen
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-muted-foreground text-center">
            Selecteer een categorie en type om te beginnen
          </p>
        </div>
      </div>
    );
  }

  const renderAssetRow = (asset: Asset) => {
    const isNew = createdAssetIds.has(asset.id);

    return (
      <button
        key={asset.id}
        onClick={() => navigate(`/asset/${asset.id}`)}
        className={`flex items-center gap-3 w-full rounded-md border px-3 py-2 text-left hover:bg-accent/50 transition-colors ${
          isNew ? 'border-primary/50 bg-primary/5' : 'bg-background'
        }`}
      >
        {/* Location icon */}
        {asset.unitId ? (
          <House className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <Buildings className="h-4 w-4 text-muted-foreground shrink-0" />
        )}

        {/* Asset info */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {asset.name}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {asset.unitId || asset.buildingId || '-'}
          </div>
        </div>

        {/* Status badge */}
        <Badge
          variant={statusVariants[asset.status] || 'secondary'}
          className="text-xs shrink-0"
        >
          {statusLabels[asset.status] || asset.status}
        </Badge>

        {/* Navigate arrow */}
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Package className="h-4 w-4" />
          Voorzieningen
          {assets.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {assets.length}
            </Badge>
          )}
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto m-3 rounded-lg border border-muted-foreground/20">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <SpinnerGap className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : assets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Package className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">Nog geen voorzieningen</p>
            <p className="text-xs mt-1">
              Selecteer locaties en klik op aanmaken
            </p>
          </div>
        ) : hasMultipleBuildings ? (
          /* Grouped by building */
          <div className="p-3 space-y-2">
            {Array.from(assetsByBuilding.entries()).map(([buildingId, buildingAssets]) => {
              const isCollapsed = collapsedBuildings.has(buildingId);
              const displayId = buildingId === '__no_building__' ? 'Geen gebouw' : buildingId;

              return (
                <div key={buildingId}>
                  {/* Building group header */}
                  <button
                    onClick={() => toggleBuilding(buildingId)}
                    className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-left hover:bg-accent/50 transition-colors"
                  >
                    {isCollapsed ? (
                      <CaretRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    ) : (
                      <CaretDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    )}
                    <Buildings className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium truncate">{displayId}</span>
                    <Badge variant="secondary" className="ml-auto text-xs shrink-0">
                      {buildingAssets.length}
                    </Badge>
                  </button>

                  {/* Assets in this building */}
                  {!isCollapsed && (
                    <div className="ml-4 mt-1 space-y-1.5">
                      {buildingAssets.map(renderAssetRow)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Flat list (single building or no grouping needed) */
          <div className="p-3 space-y-1.5">
            {assets.map(renderAssetRow)}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstancesTargetPanel;
